import { BackButton, BREAKPOINTS, TextButton as Button, COLORS, ComponentL, ComponentTextStyle, Size, SystemIcons } from "@laerdal/life-react-components";
import styled from "styled-components";
import { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageWidthFull } from "../../commonComponents/PageWidthFull";
import ProductApi from "../../services/api/ProductApi";
import SearchApi from "../../services/api/SearchApi";
import { KnowledgeArticleListItemDto } from "../../model/dto/knowledgeArticleListItemDto";
import { useDispatch, useSelector } from "react-redux";
import { buildMarketPath, selectMarket } from "../../store/slices/globalConfigSlice";
import { setCaseCreation } from "../../store/slices/chatContextSlice";
import posthog from "posthog-js";
import SamaritanTextarea from "../../commonComponents/SamaritanTextarea";
import SamaritanResponse from "../../commonComponents/SamaritanResponse";
import RelatedArticles from "../../commonComponents/RelatedArticles";
import PartsAndAccessories from "../../commonComponents/PartsAndAccessories";
import { CaseTitle, CaseSubTitle } from "./components/CommonCaseComponents";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 32px 0 56px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;



const DescriptionAreaContainer = styled.div`
  display: flex;
  
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const CreateCaseRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  justify-content: flex-end;

  ${BREAKPOINTS.MEDIUM} {
    flex-direction: row;
  }
`;

const HelpText = styled.span`
  color: ${COLORS.neutral_600};
  font-size: 16px;
`;




const CaseAssetInfoPageAI = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("cases");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentMarket = useSelector(selectMarket);

  const productName = searchParams.get("productName") || "";
  const productLineCode = searchParams.get("productLineCode") || "";
  const productSerial = searchParams.get("productSerial") || "";
  const categoryLink = searchParams.get("categoryLink") || "";
  const suboption = searchParams.get("suboption") || "technicalSupport";

  
  const [description, setDescription] = useState("");
  const [response, setResponse] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [noResponse, setNoResponse] = useState(false);
  const [cannotAnswer, setCannotAnswer] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const accumulatedResponseRef = useRef("");
  const bufferedRef = useRef(true);

  const [articles, setArticles] = useState<KnowledgeArticleListItemDto[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlePage, setArticlePage] = useState(1);
  const [articlePageCount, setArticlePageCount] = useState(1);
  const [articleTotalCount, setArticleTotalCount] = useState(0);
  const [showArticles, setShowArticles] = useState(false);
  const [articleSearchTerm, setArticleSearchTerm] = useState("");
  const articleAbortRef = useRef<AbortController | null>(null);



  const ARTICLES_PER_PAGE = 3;

  const NO_ANSWER_SENTINEL = "[NO_ANSWER]";

  const searchArticles = (query: string, page: number = 1) => {
    articleAbortRef.current?.abort();
    const controller = new AbortController();
    articleAbortRef.current = controller;

    setArticlesLoading(true);
    setArticlePage(page);

    const productLineCodes = productLineCode ? [productLineCode] : undefined;

    SearchApi.ArticleSearchWithFilters(
      query,
      page - 1,
      ARTICLES_PER_PAGE,
      productLineCodes,
      undefined,
      undefined,
      controller.signal
    )
      .then((data) => {
        const mapped = data.items.map((article) => ({
          articleNumber: article.articleNumber,
          title: article.title,
          shortPublicKnowledge: article.contentPlainText,
          description: article.contentPlainText,
          readTime: article.estimatedReadTimeMinutes ?? 0,
          viewCount: article.viewCount ?? 0,
          updatedAt: article.updatedAt,
        }));
        setArticles(mapped);
        setArticlePageCount(Math.ceil(data.totalCount / ARTICLES_PER_PAGE));
        setArticleTotalCount(data.totalCount);
        setArticlesLoading(false);
      })
      .catch((err) => {
        if (err?.name !== "AbortError" && !err?.message?.includes("canceled")) {
          setArticlesLoading(false);
        }
      });
  };

  const isMedicalDevices = categoryLink === "medical-devices";

  const handleSend = async () => {
    if (!description.trim() || isStreaming) return;

    const searchTerm = productName ? `${productName}, ${description}` : description;

    if (isMedicalDevices) {
      setShowResponse(true);
      setNoResponse(true);
      setShowArticles(true);
      setArticleSearchTerm(searchTerm);
      searchArticles(searchTerm);
      posthog?.capture?.("ProductChatSend", {
        productName,
        productLineCode,
        productSerial,
        skippedAI: true,
      });
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setResponse("");
    setIsStreaming(true);
    setNoResponse(false);
    setCannotAnswer(false);
    setShowResponse(true);
    accumulatedResponseRef.current = "";
    bufferedRef.current = true;

    setArticlePage(1);
    setArticleSearchTerm(searchTerm);
    setShowArticles(true);
    searchArticles(searchTerm);

    posthog?.capture?.("ProductChatSend", {
      productName,
      productLineCode,
      productSerial,
    });
    posthog?.capture?.("SUPP-Casedeflection-AIsummaryCTA", {
      productName,
      productLineCode,
      productSerial,
    });

    let receivedTokens = false;

    try {
      await ProductApi.ChatStream(
        {
          message: description,
          productName,
          productLineCode,
          productSerial,
          categoryLink,
        },
        (token) => {
          receivedTokens = true;
          accumulatedResponseRef.current += token;

          if (bufferedRef.current) {
            const accumulated = accumulatedResponseRef.current.trim();
            if (NO_ANSWER_SENTINEL.startsWith(accumulated)) {
              return; // still could be [NO_ANSWER], keep buffering
            }
            // not [NO_ANSWER], flush entire buffer
            bufferedRef.current = false;
            setResponse(accumulatedResponseRef.current);
          } else {
            setResponse((prev) => prev + token);
          }
        },
        controller.signal
      );
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setResponse((prev) => prev + `\n\n[Error: ${err.message}]`);
      }
    } finally {
      setIsStreaming(false);
      if (!receivedTokens) {
        setNoResponse(true);
      } else if (accumulatedResponseRef.current.trim().startsWith(NO_ANSWER_SENTINEL)) {
        setCannotAnswer(true);
        setResponse("");
      } else if (bufferedRef.current) {
        // stream ended while still buffering, flush remaining
        setResponse(accumulatedResponseRef.current);
      }
    }
  };

  const handleCreateCase = () => {
    posthog.capture('SUPP-Casedeflection-CreateCase');
    dispatch(setCaseCreation({ caseDescription: description, deflectionResponse: response }));

    const params = new URLSearchParams();
    if (productName) params.set("productName", productName);
    if (productLineCode) params.set("productLineCode", productLineCode);
    if (productSerial) params.set("productSerial", productSerial);

    const basePath = buildMarketPath(currentMarket, `/case/create/technical/${suboption}`);
    navigate(`${basePath}?${params.toString()}`);
  };

  return (
    <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
      <PageContainer>
        <Header>
          <BackButton size={Size.Small} onClick={() => window.history.back()}>
            {t("Back")}
          </BackButton>
          <CaseTitle>{t("Describe your issue")}</CaseTitle>
          <CaseSubTitle>
            {t("Please provide as much detail as possible.")}
          </CaseSubTitle>
        </Header>

        <DescriptionAreaContainer>
          <SamaritanTextarea
            placeholder={t("e.g. I have updated LLEAP software to the newest version, now my SimMan 3G is not working with it anymore, what is the issue and how can I solve it?")}
            value={description}
            onChange={(value) => setDescription(value)}
            buttonText={t("Continue")}
            minLength={20}
            onSubmit={handleSend}
            disabled={isStreaming}
          />
        </DescriptionAreaContainer>

        {showResponse && (
          <ChatContainer>
              {!isMedicalDevices && (
                <SamaritanResponse
                  isStreaming={isStreaming}
                  response={response}
                  noResponse={noResponse}
                  cannotAnswer={cannotAnswer}
                  question={description}
                />
              )}

            {!isStreaming && <PartsAndAccessories />}

            {!isStreaming && showArticles && (
              <RelatedArticles
                articles={articles}
                loading={articlesLoading}
                page={articlePage}
                pageCount={articlePageCount}
                totalCount={articleTotalCount}
                searchTerm={articleSearchTerm}
                onPageChange={(p) => searchArticles(articleSearchTerm, p)}
              />
            )}
          </ChatContainer>
        )}

        {!isStreaming && (response || noResponse || cannotAnswer) && (
          <CreateCaseRow>
            <HelpText>{t("Still need help? Let us look into it for you.")}</HelpText>
            <Button
              icon={<SystemIcons.Mail size="28px" /> }
              size={Size.Large}
              onClick={handleCreateCase}
              disabled={isStreaming}
            >
              {t("Create a support case")}
            </Button>
          </CreateCaseRow>
        )}
      </PageContainer>
    </PageWidthFull>
  );
};

export default CaseAssetInfoPageAI;
